'use client';

import React, { createContext, useContext, useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { signInWithCustomToken, signOut as firebaseSignOut, User } from '@/lib/firebase-stubs/firebase-auth';
import { getClientAuth } from '@/lib/firebase-client';
import { isValidSupabaseUUID, validateUUID, logUUIDMismatch } from '@/lib/uuid-utils';
import { useAuthManager } from '@/lib/auth/useAuthManager';

// Helper pour lire les cookies côté client
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

// 🔍 DEBUG: Context instance tracking
let contextInstanceCount = 0;

interface UnifiedAuthState {
  // Firebase Auth
  firebaseUser: any | null;
  firebaseLoading: boolean;
  firebaseError: Error | undefined;
  
  // Telegram Auth
  telegramSession: {
    verified: boolean;
    telegramId?: string;
    phoneNumber?: string;
    displayName?: string;
  } | null;
  
  // Combined state
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionCheckError: string | null;
  userProfile: {
    uid: string;
    displayName: string;
    phoneNumber?: string;
    telegramId?: string;
    authProvider: 'firebase' | 'telegram' | 'both';
  } | null;
  
  // Supabase integration
  supabaseUserId: string | null;
  isSupabaseReady: boolean;
}

interface UnifiedAuthContextType extends UnifiedAuthState {
  signIn: (firebaseToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const UnifiedAuthContext = createContext<UnifiedAuthContextType | null>(null);

interface UnifiedAuthProviderProps {
  children: React.ReactNode;
}

/**
 * UnifiedAuth Context Provider - Single source of truth for authentication
 * Eliminates multiple useUnifiedAuth hook instances and centralizes state
 */
export function UnifiedAuthProvider({ children }: UnifiedAuthProviderProps) {
  // 🔍 DEBUG: Instance tracking
  const instanceId = useRef(Math.random().toString(36).substring(7));
  
  // ✅ NEW: Use AuthManager Singleton (same as before)
  const authManagerState = useAuthManager();
  
  // 🛡️ HYDRATION GUARD - Solution pour React Error #310
  const [isHydrated, setIsHydrated] = useState(false);
  const [telegramSession, setTelegramSession] = useState<UnifiedAuthState['telegramSession']>(null);
  
  // Supabase state
  const [supabaseUserId, setSupabaseUserId] = useState<string | null>(null);
  const [isSupabaseReady, setIsSupabaseReady] = useState<boolean>(false);
  const [customClaims, setCustomClaims] = useState<any>(null);
  const [supabaseSessionUser, setSupabaseSessionUser] = useState<any>(null);
  
  const [sessionChecked, setSessionChecked] = useState(false);
  const [sessionCheckError, setSessionCheckError] = useState<string | null>(null);

  // Ref pour éviter double création utilisateur
  const isCreatingUserRef = useRef(false);
  const isCheckingSupabaseSessionRef = useRef(false);
  
  // ✅ OPTIMIZED: Extract Firebase state from AuthManager with granular memoization
  const firebaseUser = useMemo(() => authManagerState.user, [authManagerState.user]);
  
  const firebaseLoading = useMemo(() => authManagerState.isLoading, [authManagerState.isLoading]);
  
  const firebaseError = useMemo(() => 
    authManagerState.error ? new Error(authManagerState.error) : undefined,
    [authManagerState.error]
  );

  // ✅ NEW: Memoized Firebase user properties to avoid unnecessary re-renders
  const firebaseUserProperties = useMemo(() => ({
    uid: firebaseUser?.uid || null,
    displayName: firebaseUser?.displayName || null,
    email: firebaseUser?.email || null,
    phoneNumber: firebaseUser?.phoneNumber || null
  }), [firebaseUser?.uid, firebaseUser?.displayName, firebaseUser?.email, firebaseUser?.phoneNumber]);

  // ✅ NEW: Memoized Telegram session properties
  const telegramProperties = useMemo(() => ({
    verified: telegramSession?.verified || false,
    telegramId: telegramSession?.telegramId || null,
    displayName: telegramSession?.displayName || null,
    phoneNumber: telegramSession?.phoneNumber || null
  }), [telegramSession?.verified, telegramSession?.telegramId, telegramSession?.displayName, telegramSession?.phoneNumber]);
  
  // 🔍 DEBUG: Instance lifecycle tracking
  useEffect(() => {
    contextInstanceCount++;
    console.log(`[UnifiedAuthProvider:${instanceId.current}] 📊 Provider instance created. Total active: ${contextInstanceCount}`);
    console.log(`[UnifiedAuthProvider:${instanceId.current}] ✅ Centralizing authentication state for all components`);
    
    return () => {
      contextInstanceCount--;
      console.log(`[UnifiedAuthProvider:${instanceId.current}] 🗑️ Provider instance destroyed. Total remaining: ${contextInstanceCount}`);
    };
  }, []);
  
  // 🔒 STEP 1: Hydration guard
  useEffect(() => {
    if (!isHydrated && typeof window !== 'undefined') {
      console.log(`[UnifiedAuthProvider:${instanceId.current}] 🔄 Hydration completed`);
      setIsHydrated(true);
    }
  }, [isHydrated]);

  // 🔐 STEP 1.5: Vérifier les sessions Supabase (Phase 3.5)
  const checkSupabaseSession = useCallback(async () => {
    if (isCheckingSupabaseSessionRef.current) return;
    
    const sessionToken = getCookie('gandas_session');
    if (!sessionToken) {
      setSessionChecked(true);
      setSessionCheckError(null);
      return;
    }

    isCheckingSupabaseSessionRef.current = true;
    setSessionCheckError(null);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    console.log(`[UnifiedAuthProvider:${instanceId.current}] 🔐 Checking Supabase session...`);

    try {
      // Pour les sessions Telegram, on peut extraire les infos directement des cookies
      // sans appeler l'API (qui utilise Firebase/Firestore et ne connaît pas Supabase)
      const userUid = getCookie('user_uid');
      const telegramId = getCookie('telegram_id');
      const displayName = getCookie('user_display_name');
      
      if (userUid) {
        // Session Telegram détectée via cookies - pas besoin d'appel API
        console.log(`[UnifiedAuthProvider:${instanceId.current}] ✅ Telegram session detected from cookies:`, userUid);
        const user = {
          id: userUid,
          uid: userUid,
          telegramId: telegramId || undefined,
          telegram_id: telegramId || undefined,
          displayName: displayName ? decodeURIComponent(displayName) : 'User',
          display_name: displayName ? decodeURIComponent(displayName) : 'User',
          authMethod: 'telegram',
        };
        setSupabaseSessionUser(user);
        setSupabaseUserId(userUid);
        setIsSupabaseReady(true);
        return;
      }

      // Fallback: appeler l'API pour les autres types de sessions
      const response = await fetch('/api/auth/session/validate', {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      });

      if (response.status >= 500 || response.status === 429) throw new Error('Session verification unavailable');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data?.user) {
          console.log(`[UnifiedAuthProvider:${instanceId.current}] ✅ Supabase session valid:`, data.data.user.id);
          setSupabaseSessionUser(data.data.user);
          setSupabaseUserId(data.data.user.id || data.data.user.uid);
          setIsSupabaseReady(true);
        }
      } else {
        console.log(`[UnifiedAuthProvider:${instanceId.current}] ⚠️ Supabase session invalid`);
        setSupabaseSessionUser(null);
      }
    } catch (error) {
      setSessionCheckError('Impossible de vérifier votre connexion. Réessayez.');
    } finally {
      clearTimeout(timeout);
      setSessionChecked(true);
      isCheckingSupabaseSessionRef.current = false;
    }
  }, []);

  // Vérifier la session Supabase après hydration
  // ✅ SUPABASE-FIRST: Toujours vérifier la session Supabase, pas seulement si pas de Firebase
  useEffect(() => {
    if (isHydrated) {
      // Vérifier Supabase session dès l'hydration (indépendamment de Firebase)
      checkSupabaseSession();
    }
  }, [isHydrated, checkSupabaseSession]);
  
  // 🔐 Get auth instance for other operations (sign in/out)
  const auth = useMemo(() => {
    if (!isHydrated || !authManagerState.isReady) {
      return null;
    }
    return getClientAuth();
  }, [isHydrated, authManagerState.isReady]);

  // 🔄 ✅ FIXED: Stable syncUserWithSupabase function with proper dependency management
  const syncUserWithSupabase = useCallback(async (firebaseUser: User, claims?: any) => {
    if (isCreatingUserRef.current) {
      console.log('[UnifiedAuthProvider] 🔄 User sync already in progress, skipping...');
      return;
    }

    try {
      isCreatingUserRef.current = true;
      
      // 🔑 Utiliser customClaims comme source principale pour telegramId
      const telegramId = claims?.telegramId || telegramSession?.telegramId;
      
      console.log(`[UnifiedAuthProvider:${instanceId.current}] 🔄 Synchronizing Firebase user with Supabase...`, {
        firebaseUid: firebaseUser.uid,
        telegramId: telegramId,
        source: claims?.telegramId ? 'custom-claims' : 'telegram-session',
        hasTelegramSession: !!telegramSession,
        isCreatingUser: isCreatingUserRef.current
      });

      // ✅ SOLUTION : Utiliser l'API route qui utilise supabaseAdmin (bypass RLS)
      const response = await fetch('/api/auth/sync-firebase-supabase', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firebaseUid: firebaseUser.uid,
          email: firebaseUser.email,
          phoneNumber: firebaseUser.phoneNumber,
          displayName: firebaseUser.displayName || 'User',
          telegramId: telegramId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`API Error ${response.status}: ${errorData.error || 'Unknown error'}`);
      }

      const result = await response.json();
      console.log('[UnifiedAuthProvider] ✅ User successfully synced with Supabase:', result);
      
      // ✅ NOUVEAU : Validation UUID stricte avant de setter supabaseUserId
      if (result.supabaseUserId) {
        const uuidValidation = validateUUID(result.supabaseUserId);
        
        if (uuidValidation.isValid && isValidSupabaseUUID(result.supabaseUserId)) {
          console.log('[UnifiedAuthProvider] ✅ Valid Supabase UUID received and set:', result.supabaseUserId);
          setSupabaseUserId(result.supabaseUserId);
          setIsSupabaseReady(true);
        } else {
          // 🚨 CRITICAL : UUID invalide retourné par l'API - ne pas l'utiliser !
          logUUIDMismatch(result.supabaseUserId, 'syncUserWithSupabase - API Response', uuidValidation);
          console.error('[UnifiedAuthProvider] 🚨 INVALID UUID from sync API - NOT setting supabaseUserId!', {
            received: result.supabaseUserId,
            validation: uuidValidation,
            firebaseUid: firebaseUser.uid
          });
          
          // Ne pas setter un UUID invalide - garde supabaseUserId à null
          setSupabaseUserId(null);
          setIsSupabaseReady(false);
          
          throw new Error(`API returned invalid UUID format: ${uuidValidation.reason}`);
        }
      } else {
        console.warn('[UnifiedAuthProvider] ⚠️ No supabaseUserId returned from sync API');
        setSupabaseUserId(null);
        setIsSupabaseReady(false);
      }
    } catch (error) {
      console.error('[UnifiedAuthProvider] ❌ Failed to sync user with Supabase:', error);
      
      // 🛡️ PROTECTION: Éviter retry infini sur contraintes duplicate key
      if (error instanceof Error && 
          (error.message.includes('duplicate key value violates unique constraint') ||
           error.message.includes('23505'))) {
        console.warn('[UnifiedAuthProvider] 🛑 DUPLICATE KEY CONSTRAINT - blocking retry to prevent infinite loop');
        // Ne PAS remettre isCreatingUserRef à false pour empêcher les nouvelles tentatives
        return;
      }
    } finally {
      isCreatingUserRef.current = false;
    }
  }, [telegramSession?.telegramId]); // ✅ FIXED: Only depend on stable values

  // ✅ FIXED: React to Firebase Auth changes from AuthManager Singleton
  // ✅ SUPABASE-FIRST: Ne PAS effacer supabaseUserId si une session Supabase existe déjà
  useEffect(() => {
    if (!authManagerState.isReady || !firebaseUser) {
      // Ne PAS effacer Supabase state si une session Supabase/Telegram existe déjà
      // Cela permet aux utilisateurs Telegram de rester connectés sans Firebase
      if (!firebaseUser && !supabaseSessionUser) {
        // Seulement effacer si AUCUNE session n'existe (ni Firebase ni Supabase)
        setCustomClaims(null);
        // Ne pas toucher à supabaseUserId ici - il est géré par checkSupabaseSession
      }
      return;
    }
    
    console.log(`[UnifiedAuthProvider:${instanceId.current}] 🔄 AuthManager state changed, Firebase user:`, 
      firebaseUser ? `${firebaseUser.uid}` : 'No user');

    // Handle authenticated user
    if (firebaseUser) {
      console.log(`[UnifiedAuthProvider:${instanceId.current}] 👤 Processing authenticated user:`, {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        phoneNumber: firebaseUser.phoneNumber,
        displayName: firebaseUser.displayName
      });
      
      // Retrieve custom claims for telegramId
      firebaseUser.getIdTokenResult()
        .then(tokenResult => {
          if (tokenResult.claims) {
            console.log(`[UnifiedAuthProvider:${instanceId.current}] 🔑 Custom claims retrieved:`, {
              telegramId: tokenResult.claims.telegramId,
              authMethod: tokenResult.claims.authMethod
            });
            setCustomClaims(tokenResult.claims);
            
            // Synchronize with Supabase using custom claims
            syncUserWithSupabase(firebaseUser, tokenResult.claims);
          } else {
            // Sync without claims
            syncUserWithSupabase(firebaseUser);
          }
        })
        .catch(claimsError => {
          console.warn(`[UnifiedAuthProvider:${instanceId.current}] ⚠️ Failed to retrieve custom claims:`, claimsError);
          setCustomClaims(null);
          // Still sync user without claims
          syncUserWithSupabase(firebaseUser);
        });
    }
  }, [authManagerState.isReady, firebaseUser?.uid]); // ✅ FIXED: Only depend on stable user ID

  // 📱 STEP 3: Check Telegram session only AFTER hydration
  const checkTelegramSession = useCallback(async () => {
    try {
      // Only check if we have a session token
      const sessionToken = localStorage.getItem('telegramSessionToken');
      if (!sessionToken) {
        return; // Don't make API call without token
      }
      
      // Check if we have a verified Telegram session
      const response = await fetch('/api/auth/telegram/check-login-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken })
      });
      
      if (response.status >= 500 || response.status === 429) throw new Error('Session verification unavailable');
      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          setTelegramSession({
            verified: true,
            telegramId: data.telegramId,
            phoneNumber: data.phoneNumber,
            displayName: data.displayName
          });
        } else {
          setTelegramSession(null);
        }
      }
    } catch (error) {
      console.error('Failed to check Telegram session:', error);
    }
  }, []);

  useEffect(() => {
    if (!isHydrated) {
      console.log(`[UnifiedAuthProvider:${instanceId.current}] ⏳ Telegram check blocked - waiting for hydration`);
      return;
    }
    
    console.log(`[UnifiedAuthProvider:${instanceId.current}] 📱 Checking Telegram session post-hydration...`);
    
    if (typeof window !== 'undefined') {
      checkTelegramSession();
      
      // Poll for session changes only if we have a token
      const interval = setInterval(() => {
        const sessionToken = localStorage.getItem('telegramSessionToken');
        if (sessionToken) {
          checkTelegramSession();
        }
      }, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [isHydrated, checkTelegramSession]);
  
  // 🛡️ STEP 4: Compute authentication state with hydration safety and granular memoization
  // Phase 3.5: Inclure les sessions Supabase natives (priorité: Supabase > Firebase > Telegram)
  const isAuthenticated = useMemo(() => {
    if (!isHydrated) return false;
    
    // Supabase session Telegram est suffisante pour être authentifié
    if (supabaseSessionUser) {
      console.log('[UnifiedAuthContext] ✅ Authenticated via Supabase session');
      return true;
    }
    
    // Firebase ou Telegram comme fallback
    return !!(firebaseUser || telegramProperties.verified);
  }, [isHydrated, firebaseUser, telegramProperties.verified, supabaseSessionUser]);

  // ✅ SUPABASE-FIRST: Si une session Supabase existe, on n'est plus en loading
  const isLoading = useMemo(() => {
    if (!isHydrated || !sessionChecked || (sessionCheckError && !isAuthenticated)) return true;
    
    // Si une session Supabase est prête, on n'est plus en loading (même si Firebase charge encore)
    if (supabaseSessionUser && isSupabaseReady) {
      return false;
    }
    
    // Sinon, attendre Firebase
    return authManagerState.isLoading || !authManagerState.isInitialized;
  }, [isHydrated, sessionChecked, sessionCheckError, isAuthenticated, authManagerState.isLoading, authManagerState.isInitialized, supabaseSessionUser, isSupabaseReady]);
  
  // 👤 STEP 5: Build user profile with hydration safety and optimized dependencies
  // Phase 3.5: Inclure les utilisateurs Supabase natifs
  const userProfile = useMemo((): UnifiedAuthState['userProfile'] => {
    // Seulement calculer userProfile APRÈS hydration pour éviter mismatch
    if (!isHydrated || !(firebaseUser || telegramProperties.verified || supabaseSessionUser)) {
      return null;
    }

    // Déterminer le provider d'auth
    let authProvider: 'firebase' | 'telegram' | 'both' = 'firebase';
    if (supabaseSessionUser && !firebaseUser) {
      authProvider = supabaseSessionUser.authMethod === 'telegram' ? 'telegram' : 'firebase';
    } else if (firebaseUser && telegramProperties.verified) {
      authProvider = 'both';
    } else if (!firebaseUser && telegramProperties.verified) {
      authProvider = 'telegram';
    }

    // Prioriser les données Supabase si disponibles
    if (supabaseSessionUser && !firebaseUser) {
      const phoneNumber = supabaseSessionUser.phoneNumber || supabaseSessionUser.phone;
      const displayName = supabaseSessionUser.displayName || supabaseSessionUser.display_name || 'User';
      const telegramIdValue = supabaseSessionUser.telegramId || supabaseSessionUser.telegram_id;
      const telegramId = telegramIdValue ? telegramIdValue.toString() : undefined;
      
      const result: UnifiedAuthState['userProfile'] = {
        uid: supabaseSessionUser.id || supabaseSessionUser.uid,
        displayName: displayName as string,
        authProvider
      };
      
      if (phoneNumber) result.phoneNumber = phoneNumber as string;
      if (telegramId) result.telegramId = telegramId;
      
      return result;
    }
    
    const phoneNumber = firebaseUserProperties.phoneNumber || telegramProperties.phoneNumber;
    const telegramId = customClaims?.telegramId || telegramProperties.telegramId;
    
    const result: UnifiedAuthState['userProfile'] = {
      uid: firebaseUserProperties.uid || `telegram_${telegramProperties.telegramId}`,
      displayName: firebaseUserProperties.displayName || telegramProperties.displayName || 'User',
      authProvider
    };
    
    if (phoneNumber) result.phoneNumber = phoneNumber;
    if (telegramId) result.telegramId = telegramId;
    
    return result;
  }, [isHydrated, firebaseUser, firebaseUserProperties, telegramProperties, customClaims?.telegramId, supabaseSessionUser]);
  
  // Sign in with Firebase token - stabilisé avec useCallback
  const signIn = useCallback(async (firebaseToken: string) => {
    if (!auth) {
      throw new Error('Firebase not initialized');
    }
    
    try {
      await signInWithCustomToken(auth, firebaseToken);
      localStorage.setItem('firebaseToken', firebaseToken);
    } catch (error) {
      console.error('Failed to sign in with Firebase:', error);
      throw error;
    }
  }, [auth]);
  
  // Sign out from both systems - stabilisé avec useCallback
  // Phase 3.5: Révoquer aussi les sessions Supabase
  const signOut = useCallback(async () => {
    // Clear Firebase
    if (auth && firebaseUser) {
      await firebaseSignOut(auth);
    }
    localStorage.removeItem('firebaseToken');
    
    // Clear Telegram session
    localStorage.removeItem('telegramSessionToken');
    setTelegramSession(null);
    
    // ✅ Phase 3.5: Révoquer la session Supabase via API
    const sessionToken = getCookie('gandas_session');
    if (sessionToken) {
      try {
        await fetch('/api/auth/session/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionToken }),
          credentials: 'include',
        });
        console.log('[UnifiedAuthProvider] ✅ Supabase session revoked');
      } catch (error) {
        console.warn('[UnifiedAuthProvider] ⚠️ Failed to revoke Supabase session:', error);
      }
    }
    
    // Clear Supabase state
    setSupabaseUserId(null);
    setIsSupabaseReady(false);
    setSupabaseSessionUser(null);
    
    // Clear any other auth-related storage
    if (typeof window !== 'undefined') {
      const keysToRemove = Object.keys(localStorage).filter(key => 
        key.startsWith('telegram_') || key.includes('session')
      );
      keysToRemove.forEach(key => localStorage.removeItem(key));
    }
  }, [auth, firebaseUser]);
  
  // Refresh authentication state - stabilisé avec useCallback
  const refreshAuth = useCallback(async () => {
    if (!isHydrated || typeof window === 'undefined') {
      return;
    }
    
    // Re-check Supabase session (cookies gandas_session / user_uid)
    await checkSupabaseSession();

    // Re-check Firebase token
    const storedToken = localStorage.getItem('firebaseToken');
    if (storedToken && auth && !firebaseUser) {
      try {
        await signInWithCustomToken(auth, storedToken);
      } catch (error) {
        console.error('Failed to refresh Firebase auth:', error);
        localStorage.removeItem('firebaseToken');
      }
    }
    
    // Re-check Telegram session
    await checkTelegramSession();
  }, [auth, firebaseUser, checkSupabaseSession, checkTelegramSession, isHydrated]);

  // ✅ MEMOIZED CONTEXT VALUE to prevent unnecessary re-renders
  const contextValue = useMemo((): UnifiedAuthContextType => ({
    firebaseUser,
    firebaseLoading,
    firebaseError,
    telegramSession,
    isAuthenticated,
    isLoading,
    sessionCheckError,
    userProfile,
    supabaseUserId,
    isSupabaseReady,
    signIn,
    signOut,
    refreshAuth
  }), [
    firebaseUser,
    firebaseLoading,
    firebaseError,
    telegramSession,
    isAuthenticated,
    isLoading,
    sessionCheckError,
    userProfile,
    supabaseUserId,
    isSupabaseReady,
    signIn,
    signOut,
    refreshAuth
  ]);

  return (
    <UnifiedAuthContext.Provider value={contextValue}>
      {children}
    </UnifiedAuthContext.Provider>
  );
}

/**
 * Custom hook to use the UnifiedAuth context
 * Throws an error if used outside of UnifiedAuthProvider
 */
export function useUnifiedAuthContext(): UnifiedAuthContextType {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error('useUnifiedAuthContext must be used within a UnifiedAuthProvider');
  }
  return context;
}

/**
 * Helper to require authentication
 */
export function useRequireAuth(redirectTo: string = '/auth/telegram-register') {
  const { isAuthenticated, isLoading } = useUnifiedAuthContext();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setShouldRedirect(true);
      window.location.href = redirectTo;
    }
  }, [isAuthenticated, isLoading, redirectTo]);
  
  return { isAuthenticated, isLoading, shouldRedirect };
}